// src/components/ProductsNavRow.jsx
import React, { useState, useRef, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { ChevronLeft, ChevronRight, ChevronDown, Menu } from "lucide-react";

const ProductsNavRow = () => {
  const links = [
    { path: "/products/mep_products", label: "MEP" },
    { path: "/products/tiling_flooring_products", label: "Tiling & Flooring" },
    { path: "/products/paintaing_decoration_products", label: "Painting & Decoration" },
    { path: "/products/ceiling_cladding_products", label: "Ceiling & Cladding" },
    { path: "/products/tools_equipment_products", label: "Tools & Equipment" },
    { path: "/products/finishes_decoation_products", label: "Finishes & Decoration" },
    { path: "/products/building_hardware_products", label: "Building Hardware" },
    { path: "/products/sustainable_green_solution", label: "Green Solutions" },
    { path: "/products/assesories", label: "Accessories" },
    { path: "/products/construction_chemical", label: "Construction Chemical" },
  ];

  const containerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const checkScroll = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (containerRef.current) {
      const scrollAmount = 200;
      containerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleResize = () => {
    const mobile = window.innerWidth < 768;
    setIsMobile(mobile);
    // Close dropdown when switching to desktop view
    if (!mobile) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      window.addEventListener('resize', handleResize);
      checkScroll(); // Initial check
      
      return () => {
        container.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  return (
    <div className="relative bg-white shadow-md border-t border-b border-gray-200">
      {/* Mobile Dropdown Toggle */}
      {isMobile && (
        <button 
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="w-full flex items-center justify-between px-4 py-3 text-gray-700 font-medium"
          aria-expanded={isDropdownOpen}
        >
          <span>Product Categories</span>
          <ChevronDown 
            className={`h-5 w-5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} 
          />
        </button>
      )}

      {/* Desktop Navigation */}
      {!isMobile && (
        <>
          {/* Navigation Arrows */}
          {showLeftArrow && (
            <button 
              onClick={() => scroll('left')}
              className="absolute left-0 top-0 bottom-0 z-10 bg-white opacity-90 hover:opacity-100 px-2 flex items-center justify-center transition-opacity"
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            </button>
          )}
          
          {showRightArrow && (
            <button 
              onClick={() => scroll('right')}
              className="absolute right-0 top-0 bottom-0 z-10 bg-white opacity-90 hover:opacity-100 px-2 flex items-center justify-center transition-opacity"
              aria-label="Scroll right"
            >
              <ChevronRight className="h-5 w-5 text-gray-600" />
            </button>
          )}
        </>
      )}

      {/* Navigation Links Container */}
      <div 
        ref={containerRef}
        className={`${isMobile ? 
          (isDropdownOpen ? 'max-h-96 overflow-y-auto' : 'max-h-0 overflow-hidden') + 
          ' transition-all duration-300 ease-in-out' : 
          'flex overflow-x-auto scrollbar-hide px-2 py-3 scroll-smooth'
        }`}
        style={!isMobile ? { scrollbarWidth: 'none', msOverflowStyle: 'none' } : {}}
      >
        <div className={isMobile ? "py-2" : "flex space-x-2 px-2"}>
          {links.map((link, index) => (
            <NavLink
              key={index}
              to={link.path}
              onClick={() => isMobile && setIsDropdownOpen(false)}
              className={({ isActive }) =>
                isMobile ? 
                `block px-4 py-3 text-sm font-medium transition-all duration-200 ${isActive
                  ? "bg-blue-100 text-blue-700 border-l-4 border-blue-700"
                  : "text-gray-700 hover:bg-gray-100"
                }` :
                `whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${isActive
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-gray-700 bg-gray-100 hover:bg-blue-100 hover:text-blue-600"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductsNavRow;